/* Hallmark · component: DateField · genre: modern-minimal · theme: maeiz-tokens
 * Custom calendar popover — not native date input
 */
import React, { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { clsx } from "clsx";
import { FormField } from "../FormField";
import { Button } from "../Button";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { Calendar } from "../Calendar";

function formatDisplay(iso?: string) {
    if (!iso) return null;
    const [y, m, d] = iso.split("-");
    if (!y || !m || !d) return iso;
    return `${m} / ${d} / ${y}`;
}

export interface DateFieldProps {
    id?: string;
    label: string;
    description?: string;
    error?: string;
    required?: boolean;
    fullWidth?: boolean;
    value?: string;
    defaultValue?: string;
    onChange?: (value: string) => void;
    min?: string;
    max?: string;
    disabled?: boolean;
    className?: string;
    placeholder?: string;
}

export const DateField: React.FC<DateFieldProps> = ({
    id,
    label,
    description,
    error,
    required,
    fullWidth = true,
    value: controlled,
    defaultValue,
    onChange,
    min,
    max,
    disabled,
    className,
    placeholder = "mm / dd / yyyy",
}) => {
    const [internal, setInternal] = useState(defaultValue ?? "");
    const [open, setOpen] = useState(false);
    const value = controlled ?? internal;
    const fieldId = id ?? `date-${label.replace(/\s+/g, "-").toLowerCase()}`;

    const setValue = (next: string) => {
        if (controlled === undefined) setInternal(next);
        onChange?.(next);
        setOpen(false);
    };

    return (
        <FormField
            id={fieldId}
            label={label}
            description={description}
            error={error}
            required={required}
            className={clsx(fullWidth && "w-full", className)}
        >
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id={fieldId}
                        type="button"
                        variant="outline"
                        disabled={disabled}
                        aria-invalid={!!error}
                        className={clsx(
                            "w-full justify-start font-normal tabular-nums",
                            !value && "text-neutral-secondary",
                        )}
                        icon={CalendarIcon}
                        iconPosition="start"
                    >
                        <span className="truncate">{formatDisplay(value) ?? placeholder}</span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-2">
                    <Calendar value={value} onChange={setValue} min={min} max={max} />
                </PopoverContent>
            </Popover>
        </FormField>
    );
};
